import api from '@/services/api.service';
import { formatQuantity } from '@/utils/format';

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

export type EngineeringCalcMethod = 'GEOMETRY' | 'PUBLISHED' | 'FIXED' | 'MANUAL';
export type EngineeringStatus = 'DRAFT' | 'APPROVED' | 'INACTIVE';

export interface EngineeringProfile {
  code: string;
  label: string;
  params: string[];
  optional_params: string[];
  methods: EngineeringCalcMethod[];
  allows_area_basis: boolean;
}

export interface EngineeringConfig {
  available: boolean;
  enabled: boolean;
  formula_revision: string;
  calc_methods: EngineeringCalcMethod[];
  fixed_weight_bases: string[];
  param_labels: Record<string, string>;
  profiles: EngineeringProfile[];
}

export interface EngineeringGrade {
  id: string;
  family: string;
  grade_code: string;
  description?: string | null;
  density_g_cm3: number | string;
  density_source?: string | null;
  status: EngineeringStatus;
  updated_at?: string;
}

export interface EngineeringSection {
  id: string;
  profile_code: string;
  designation: string;
  standard?: string | null;
  kg_per_m: number | string;
  source_reference?: string | null;
  status: EngineeringStatus;
}

export interface ApprovedEngineeringDefinition {
  definition_id: string;
  item_id: string;
  item_code: string;
  revision: number;
  approved_at: string;
  profile_code: string;
  calc_method: EngineeringCalcMethod;
  engineering_standard: string | null;
  grade: { id: string; family: string; grade_code: string; density_source: string | null } | null;
  density_g_cm3: number | null;
  section: { id: string; designation: string; standard: string | null; source_reference: string | null } | null;
  kg_per_m: number | null;
  fixed_unit_weight: number | null;
  fixed_weight_basis: string | null;
  standard_dimensions: Record<string, number>;
  dimensions_variable: boolean;
  tolerance_plus_pct: number | null;
  tolerance_minus_pct: number | null;
  formula_revision: string;
}

export interface ItemEngineeringDefinition {
  id: string;
  item_id: string;
  profile_code: string;
  calc_method: EngineeringCalcMethod;
  material_grade_id: string | null;
  section_weight_id: string | null;
  engineering_standard: string | null;
  standard_dimensions: Record<string, number>;
  dimensions_variable: boolean;
  fixed_unit_weight: number | string | null;
  fixed_weight_basis: string | null;
  tolerance_plus_pct: number | string | null;
  tolerance_minus_pct: number | string | null;
  status: EngineeringStatus;
  revision: number;
  approved_at?: string | null;
}

export interface ItemEngineeringBundle {
  available: boolean;
  definition: ItemEngineeringDefinition | null;
  approved: ApprovedEngineeringDefinition | null;
  revisions: Array<{ revision: number; approved_at: string; approved_by: string | null }>;
}

export interface OverrideInput {
  value: number | null;
  reason?: string | null;
}

export interface EngineeringLineInput {
  dimensions?: Record<string, number | null> | null;
  density_override?: OverrideInput | null;
  manual_unit_weight?: OverrideInput | null;
  refresh?: boolean;
}

export interface EngineeringCalcResult {
  status: 'CALCULATED' | 'INCOMPLETE';
  reason?: string;
  cross_section_area_mm2: number | null;
  kg_per_m: number | null;
  unit_weight_kg: number | null;
  basis: 'PIECES' | 'LENGTH' | 'AREA' | 'MASS' | null;
  physical_quantity: number | null;
  physical_uom: string | null;
  total_weight_kg: number | null;
  warnings: string[];
}

export interface OverrideRecord {
  original: number | null;
  value: number;
  reason: string;
  by: string;
  at: string;
}

export interface EngineeringLineSnapshot {
  schema: number;
  status: 'CALCULATED' | 'INCOMPLETE';
  reason?: string;
  definition: ApprovedEngineeringDefinition;
  inputs: {
    dimensions: Record<string, number>;
    density_override?: OverrideRecord;
    manual_unit_weight?: OverrideRecord;
  };
  result: EngineeringCalcResult;
  calculated_at: string;
  calculated_by: string;
}

export interface EngineeringPreview {
  snapshot: EngineeringLineSnapshot;
  theoretical_unit_weight: number | null;
  theoretical_weight: number | null;
  theoretical_weight_uom: string | null;
  can_override: boolean;
}

export interface EngineeringPreviewRequest {
  item_code: string;
  pr_item_id?: string | null;
  engineering_input?: EngineeringLineInput | null;
  quantity: number;
  uom?: string | null;
  piece_quantity?: number | null;
  measurement_type?: string | null;
  length_mm?: number | null;
  width_mm?: number | null;
}

export function parseEngineeringSnapshot(raw: unknown): EngineeringLineSnapshot | null {
  if (raw == null || raw === '') return null;
  try {
    const s = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return s && typeof s === 'object' && (s as EngineeringLineSnapshot).definition ? (s as EngineeringLineSnapshot) : null;
  } catch {
    return null;
  }
}

export function formatKg(value: unknown, digits = 3): string {
  return formatQuantity(value, { maximumFractionDigits: digits, unit: 'kg', nullDisplay: '—' }).replace(/^— kg$/, '—');
}

class EngineeringMaterialService {
  async getConfig(): Promise<EngineeringConfig> {
    return unwrap(await api.getClient().get('/inventory/engineering/config'));
  }

  async setEnabled(enabled: boolean): Promise<{ enabled: boolean }> {
    return unwrap(await api.getClient().put('/inventory/engineering/config', { enabled }));
  }

  async listGrades(includeInactive = false): Promise<EngineeringGrade[]> {
    return unwrap(await api.getClient().get('/inventory/engineering/grades', { params: { include_inactive: includeInactive } }));
  }

  async createGrade(body: Partial<EngineeringGrade>): Promise<EngineeringGrade> {
    return unwrap(await api.getClient().post('/inventory/engineering/grades', body));
  }

  async updateGrade(id: string, body: Partial<EngineeringGrade>): Promise<EngineeringGrade> {
    return unwrap(await api.getClient().put(`/inventory/engineering/grades/${id}`, body));
  }

  async setGradeStatus(id: string, status: 'APPROVED' | 'INACTIVE'): Promise<EngineeringGrade> {
    return unwrap(await api.getClient().post(`/inventory/engineering/grades/${id}/status`, { status }));
  }

  async listSections(includeInactive = false): Promise<EngineeringSection[]> {
    return unwrap(await api.getClient().get('/inventory/engineering/sections', { params: { include_inactive: includeInactive } }));
  }

  async createSection(body: Partial<EngineeringSection>): Promise<EngineeringSection> {
    return unwrap(await api.getClient().post('/inventory/engineering/sections', body));
  }

  async updateSection(id: string, body: Partial<EngineeringSection>): Promise<EngineeringSection> {
    return unwrap(await api.getClient().put(`/inventory/engineering/sections/${id}`, body));
  }

  async setSectionStatus(id: string, status: 'APPROVED' | 'INACTIVE'): Promise<EngineeringSection> {
    return unwrap(await api.getClient().post(`/inventory/engineering/sections/${id}/status`, { status }));
  }

  async getItemDefinition(itemId: string): Promise<ItemEngineeringBundle> {
    return unwrap(await api.getClient().get(`/inventory/engineering/items/${itemId}`));
  }

  async saveItemDefinition(itemId: string, body: Record<string, unknown>): Promise<ItemEngineeringBundle> {
    return unwrap(await api.getClient().put(`/inventory/engineering/items/${itemId}`, body));
  }

  async approveItemDefinition(itemId: string): Promise<ItemEngineeringBundle> {
    return unwrap(await api.getClient().post(`/inventory/engineering/items/${itemId}/approve`, {}));
  }

  async getApprovedByCodes(codes: string[]): Promise<Record<string, ApprovedEngineeringDefinition>> {
    const unique = [...new Set(codes.map((c) => String(c || '').trim()).filter(Boolean))];
    if (!unique.length) return {};
    return unwrap(await api.getClient().get('/inventory/engineering/items/approved', { params: { codes: unique.join(',') } }));
  }

  async calculate(body: EngineeringPreviewRequest): Promise<EngineeringPreview> {
    return unwrap(await api.getClient().post('/inventory/engineering/calculate', body));
  }
}

export const engineeringMaterialService = new EngineeringMaterialService();
export default engineeringMaterialService;
